import { DefaultOption } from "@smartsuite/react-ui/lib/components/selects/DefaultOption/DefaultOption";
import { FieldLabel } from "@smartsuite/react-ui/lib/components/forms/FieldLabel/FieldLabel";
import { InputGroup } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup";
import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib/components/selects/CustomSelect/CustomSelect";
import { NumberInputField } from "@smartsuite/react-ui/lib/components/forms/NumberInputField/NumberInputField";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { Textarea } from "@smartsuite/react-ui/lib/components/forms/Textarea/Textarea";
import { useSelectState } from "@smartsuite/react-ui/lib/components/selects/useSelectState/useSelectState";
import { CheckmarkOption } from "@smartsuite/react-ui/lib/components/selects/components/CheckmarkOption/CheckmarkOption";
import React, { useRef } from "react";
import { hasTemplateAttr } from "../../../canvas/state/attributes";
import {
  DocumentDesignItem,
  DocumentDesignItemType,
  FileItemDisplayMode,
  FileItemFitMode,
  isItemOfType,
} from "../../../canvas/state/types";
import { StyledSwitchButton } from "../../StyledSwitchButton/StyledSwitchButton";
import { showElementWithoutImplementation } from "../../utils";
import { DirectionsToggles } from "../DirectionsToggles/DirectionsToggles";
import {
  defaultImagePadding,
  defaultImageSize,
  displayModeOptions,
  fitModeOptions,
  sizingOptions,
} from "./SidebarSelectionConfigFile.constants";

export interface SidebarSelectionConfigFileProps {
  selected: DocumentDesignItem;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

export const SidebarSelectionConfigFile: React.FC<
  SidebarSelectionConfigFileProps
> = ({ selected, onChangeSelected }) => {
  const [sizingSelectState, setSizingSelectState] = useSelectState();
  const [imagePaddingSelectState, setImagePaddingSelectState] =
    useSelectState();
  const [imageSizeSelectState, setImageSizeSelectState] = useSelectState();
  const imagePaddingRef = useRef<HTMLInputElement>(null);
  const imageSizeRef = useRef<HTMLInputElement>(null);

  const displayMode = isItemOfType(selected, DocumentDesignItemType.file)
    ? (selected.displayMode ?? "first-image")
    : null;

  return (
    <>
      {isItemOfType(selected, DocumentDesignItemType.file) && (
        <>
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text weight="medium" wrapMode="ellipsis">
                Display Mode
              </Text>
            </div>
            <StyledSwitchButton
              options={displayModeOptions}
              currentValue={displayMode ?? "first-image"}
              onChange={(value) =>
                onChangeSelected({
                  ...selected,
                  displayMode: value as FileItemDisplayMode,
                })
              }
            />
          </div>

          {displayMode === "first-image" && (
            <div>
              <div style={{ marginBottom: 4 }}>
                <Text weight="medium" wrapMode="ellipsis">
                  Fit Mode
                </Text>
              </div>
              <StyledSwitchButton
                options={fitModeOptions}
                currentValue={selected.fitMode ?? "stretch"}
                onChange={(value) =>
                  onChangeSelected({
                    ...selected,
                    fitMode: value as FileItemFitMode,
                  })
                }
              />
            </div>
          )}

          {displayMode === "all-images" && (
            <>
              <div>
                <FieldLabel>Sizing</FieldLabel>
                <KeyValueCustomSelect<
                  "value",
                  "label",
                  (typeof sizingOptions)[0]
                >
                  state={sizingSelectState}
                  showClear={false}
                  options={sizingOptions}
                  placeholderArrowStyle="filled-dark"
                  renderOption={(option, active) => (
                    <CheckmarkOption
                      rightAlignSelected={true}
                      selected={active}
                    >
                      {option.label}
                    </CheckmarkOption>
                  )}
                  renderSelectedOption={(option) => (
                    <DefaultOption selected={true}>
                      {option.label}
                    </DefaultOption>
                  )}
                  value={selected.sizingMode ?? "square"}
                  onChange={(value) =>
                    onChangeSelected({ ...selected, sizingMode: value })
                  }
                  onOpenedChange={setSizingSelectState}
                />
              </div>

              <div className="sidebar-selection-config__section__split-column">
                <div>
                  <FieldLabel>Image Size</FieldLabel>
                  <NumberInputField
                    ref={imageSizeRef}
                    onFocus={() => setImageSizeSelectState(true)}
                    onBlur={() => setImageSizeSelectState(false)}
                    state={imageSizeSelectState ? "active" : undefined}
                    showSymbol={false}
                    autoHideNavigation={true}
                    inputStyles={{
                      backgroundColor: "white",
                      borderColor: "G-1-5",
                      borderHoverColor: "G-3",
                      borderFocusColor: "G-3",
                    }}
                    max="1000"
                    min="1"
                    value={`${selected.imageSize ?? defaultImageSize}`}
                    onChangeValue={(value) => {
                      if (imageSizeRef.current) imageSizeRef.current.focus();

                      const parsedImageSize = parseInt(value);
                      if (!isNaN(parsedImageSize) && parsedImageSize > 0) {
                        onChangeSelected({
                          ...selected,
                          imageSize: parsedImageSize,
                        });
                      }
                    }}
                  />
                </div>

                <div>
                  <FieldLabel>Image Padding</FieldLabel>
                  <NumberInputField
                    ref={imagePaddingRef}
                    onFocus={() => setImagePaddingSelectState(true)}
                    onBlur={() => setImagePaddingSelectState(false)}
                    state={imagePaddingSelectState ? "active" : undefined}
                    showSymbol={false}
                    autoHideNavigation={true}
                    inputStyles={{
                      backgroundColor: "white",
                      borderColor: "G-1-5",
                      borderHoverColor: "G-3",
                      borderFocusColor: "G-3",
                    }}
                    max="1000"
                    min="0"
                    value={`${selected.imagePadding ?? defaultImagePadding}`}
                    onChangeValue={(value) => {
                      if (imagePaddingRef.current)
                        imagePaddingRef.current.focus();

                      const parsedImagePadding = parseInt(value);
                      if (
                        !isNaN(parsedImagePadding) &&
                        parsedImagePadding >= 0
                      ) {
                        onChangeSelected({
                          ...selected,
                          imagePadding: parsedImagePadding,
                        });
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {hasTemplateAttr(selected) && (
        <div>
          <FieldLabel>Content</FieldLabel>
          <InputGroup>
            <Textarea
              value={selected.template}
              onChange={(event) =>
                onChangeSelected({ ...selected, template: event.target.value })
              }
            />
          </InputGroup>
        </div>
      )}

      {showElementWithoutImplementation && <DirectionsToggles title="Border" />}
    </>
  );
};
